// src/components/Header.jsx
import React, { useState, useEffect } from 'react';
import './Header.css';
import { syncAllMatches } from '../services/syncService';

function Header({ currentUser, onLogout, onOpenProfile, onOpenPlayers, showUserInfo = true, hideHeader = false }) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  
  // 🆕 État de la synchronisation intelligente
  const [syncStatus, setSyncStatus] = useState('idle'); // idle, syncing, success, error
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [syncedCount, setSyncedCount] = useState(0);


  // Écouter les changements de connexion
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // 🆕 Synchronisation automatique intelligente
  useEffect(() => {
    const autoSync = async () => {
      if (!navigator.onLine) return;
      
      try {
        console.log('🔄 Synchronisation automatique intelligente...');
        setSyncStatus('syncing');
        
        const result = await syncAllMatches();
        
        console.log(`✅ ${result.synced} matchs synchronisés, ${result.skipped} skippés`);
        
        setSyncedCount(result.synced);
        setSyncStatus('success');
        setLastSyncTime(new Date());
        
        // Retour à idle après 3 secondes
        setTimeout(() => setSyncStatus('idle'), 3000);
        
      } catch (error) {
        console.error('❌ Erreur synchronisation:', error);
        setSyncStatus('error');
        setTimeout(() => setSyncStatus('idle'), 5000);
      }
    };

    // Première synchro après 5 secondes
    const initialTimer = setTimeout(autoSync, 5000);
    
    // Puis toutes les 2 minutes
    const interval = setInterval(autoSync, 2 * 60 * 1000);

    return () => {
      clearTimeout(initialTimer);
      clearInterval(interval);
    };
  }, []);

  // 🆕 Synchronisation manuelle
  const handleManualSync = async () => {
    if (!isOnline) {
      alert('⚠️ Pas de connexion Internet');
      return;
    }
    
    if (syncStatus === 'syncing') return;
    
    try {
      setSyncStatus('syncing');
      
      const result = await syncAllMatches();
      
      console.log(`✅ ${result.synced} matchs synchronisés, ${result.skipped} skippés`);
      
      setSyncedCount(result.synced);
      setSyncStatus('success');
      setLastSyncTime(new Date());
      
      setTimeout(() => setSyncStatus('idle'), 3000);
      
    } catch (error) {
      console.error('❌ Erreur synchronisation:', error);
      setSyncStatus('error');
      setTimeout(() => setSyncStatus('idle'), 5000);
    }
  };

  // Formatage du temps écoulé
  const getTimeAgo = () => {
    if (!lastSyncTime) return '';
    
    const seconds = Math.floor((new Date() - lastSyncTime) / 1000);
    
    if (seconds < 60) return 'à l\'instant';
    if (seconds < 3600) return `il y a ${Math.floor(seconds / 60)}min`;
    return `il y a ${Math.floor(seconds / 3600)}h`;
  };

  if (hideHeader) {
    return null;
  }

  return (
    <header className="app-header">
      {/* Logo + Nom application */}
      <div className="header-left">
        <svg className="app-icon" viewBox="0 0 100 100" width="32" height="32">
          <circle cx="50" cy="50" r="45" fill="#9FD356" stroke="#6FA832" strokeWidth="2" />
          <path d="M 20 30 Q 35 50 20 70" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" />
          <path d="M 80 30 Q 65 50 80 70" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" />
        </svg>
        <h1 className="app-title">TenStats</h1>
      </div>

      {/* 🆕 Centre : Indicateur de synchronisation intelligente */}
      <div className="header-center">
        {syncStatus === 'syncing' && (
          <div className="sync-indicator syncing">
            <span className="sync-spinner">⟳</span> Synchronisation...
          </div>
        )}
        {syncStatus === 'success' && syncedCount > 0 && (
          <div className="sync-indicator success">
            ✅ {syncedCount} synchronisé{syncedCount > 1 ? 's' : ''} {getTimeAgo()}
          </div>
        )}
        {syncStatus === 'success' && syncedCount === 0 && (
          <div className="sync-indicator success">
            ✅ Tout à jour {getTimeAgo()}
          </div>
        )}
        {syncStatus === 'error' && (
          <div className="sync-indicator error">
            ❌ Erreur de synchronisation
          </div>
        )}
      </div>

      {/* Boutons de droite */}
      <div className="header-right">
        {/* Indicateur de connexion */}
        <div className="connection-status" title={isOnline ? 'Connecté à Internet' : 'Hors ligne'}>
          <span className={`status-dot ${isOnline ? 'online' : 'offline'}`}></span>
          <span className="status-text">
            {isOnline ? 'En ligne' : 'Hors ligne'}
          </span>
        </div>

        {/* 🆕 Bouton de synchronisation manuelle */}
        <button
          className="sync-button-manual"
          onClick={handleManualSync}
          disabled={!isOnline || syncStatus === 'syncing'}
          title="Synchroniser maintenant"
        >
          <span className={syncStatus === 'syncing' ? 'spinning' : ''}>⟳</span>
        </button>

        {/* Info utilisateur */}
        {showUserInfo && currentUser && (
          <>
            {/* Bouton Joueurs */}
            {onOpenPlayers && (
              <button
                className="header-button"
                onClick={onOpenPlayers}
                title="Mes joueurs"
                aria-label="Mes joueurs"
              >
                <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor" aria-hidden="true" style={{ display: 'block' }}>
                  <circle cx="8.5" cy="8" r="3.4" />
                  <path d="M1.8 20.5c0-3.9 3-6.6 6.7-6.6s6.7 2.7 6.7 6.6z" />
                  <circle cx="17.2" cy="9" r="2.8" opacity=".75" />
                  <path d="M16.2 14.2c3.6-.3 6.1 2 6.1 6.3h-4.9c0-2.3-.5-4.4-1.2-6.3z" opacity=".75" />
                </svg>
              </button>
            )}
            
            {/* 🆕 Bouton Profil */}
            {onOpenProfile && (
              <button
                className="header-button"
                onClick={onOpenProfile}
                title="Mon profil"
              >
                ⚙️
              </button>
            )}
          </>
        )}

        {/* Bouton déconnexion */}
        {showUserInfo && currentUser && onLogout && (
          <button className="logout-button" onClick={onLogout} title="Se déconnecter">
            Déconnexion
          </button>
        )}
      </div>
    </header>
  );
}

export default Header;